import { REPORT_WINDOW_MAX_DAYS } from '@fieldvision/contracts';
import { create } from 'zustand';

import { daysBetween, endOfDayIso, startOfDayIso, toDateInput } from './format.js';

/** A chosen outlet or petugas: the id the api filters by, and the name shown for it. */
export interface PickedOption {
  id: string;
  label: string;
}

/**
 * The report filter, held once for the whole console.
 *
 * A store rather than page state because the three report tabs, the export
 * button and the price index all read the same window. Local state per tab
 * means switching from Leaderboard to Planogram silently resets the range a
 * manager just set, and the numbers change for a reason they did not choose.
 *
 * The dates are held as `YYYY-MM-DD` because that is what `<input type="date">`
 * speaks. Conversion to the ISO instants the API wants happens in `toQuery`,
 * which is also where the end of the day gets widened.
 */
interface ReportFilterState {
  from: string;
  to: string;
  /**
   * EVERY DISTRIBUTOR CHOSEN, empty for "Semua Distributor" — a list since the
   * filter became multi-select. Kept in the picker's own order (the region
   * list's), so ticking the same two in a different order is the same filter and
   * the same cache key. `regionParam` is how it goes on the wire.
   */
  regionIds: string[];
  /**
   * EVERY OUTLET AND PETUGAS CHOSEN, empty for "Semua" — lists since both became
   * multi-select.
   *
   * THE LABELS ARE STORED BESIDE THE IDS, and that is not redundancy. Outlets and
   * salespeople are picked from one small page of search results at a time, so a
   * chosen row is usually not in the twenty the picker happens to be holding when
   * the screen is next mounted — and there is no endpoint that turns one id back
   * into one name. Keeping only the ids means switching from Leaderboard to
   * Planogram redraws the filter bar unable to name what the report is still
   * narrowed to.
   *
   * The labels are display only: `toQuery` and `filterKey` read the ids, so a
   * renamed outlet shows a stale name until it is re-picked and no request is
   * ever affected.
   */
  outlets: PickedOption[];
  salesUsers: PickedOption[];
  /**
   * Ids since 0044, where both became master data. The value bound to the select
   * is no longer the label shown in it, and the API filters by id — a name-keyed
   * filter stops matching the moment somebody renames the channel.
   *
   * LISTS, like `regionIds`, since both became multi-select; empty means "Semua".
   * The api combines them per channel — a chosen channel with no ticked
   * classification is taken whole — see `channelClassificationFilter` there.
   */
  channelIds: string[];
  classificationIds: string[];
  set: (patch: Partial<Omit<ReportFilterState, 'set' | 'reset'>>) => void;
  reset: () => void;
}

const defaults = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86_400_000);
  return {
    from: toDateInput(thirtyDaysAgo),
    to: toDateInput(today),
    regionIds: [],
    outlets: [],
    salesUsers: [],
    channelIds: [],
    classificationIds: [],
  };
};

export const useReportFilters = create<ReportFilterState>()((set) => ({
  ...defaults(),
  /**
   * CLEARING EVERY CHANNEL CLEARS THE CLASSIFICATIONS, in the store rather than
   * in the control that happens to have triggered it.
   *
   * The picker is disabled until a channel is chosen, so a classification held
   * over past the last channel would narrow every report while its control
   * showed nothing to untick. Enforcing it here means it holds for every caller:
   * the picker, the reset, a chip, anything added later.
   *
   * Unticking ONE channel of several prunes only that channel's classifications,
   * which needs the classification list — `FilterBar` does that with
   * `pruneClassifications` in the same `set`.
   */
  set: (patch) =>
    set(() =>
      patch.channelIds !== undefined && patch.channelIds.length === 0
        ? { ...patch, classificationIds: [] }
        : patch,
    ),
  reset: () => set(defaults()),
}));

export interface ReportFilterValues {
  from: string;
  to: string;
  regionIds: readonly string[];
  outlets: readonly PickedOption[];
  salesUsers: readonly PickedOption[];
  channelIds: readonly string[];
  classificationIds: readonly string[];
}

/**
 * The window the API will reject, detected before the request.
 *
 * `/reporting/*` answers 400 for a window over 90 days or an inverted range.
 * Surfacing that as a validation message under the date field is the difference
 * between "you asked for too much" and "the report is broken".
 *
 * `maxDays` EXISTS BECAUSE THE CAP IS NOT UNIVERSAL. `/price-index/*` takes the
 * same filters with every one optional and NO ceiling — deliberately, since a
 * pricing comparison over a season is the question that endpoint answers.
 * Passing `null` drops only the ceiling check; a missing or inverted range is
 * still wrong everywhere. The alternative — showing a cap warning on a screen
 * whose request would have succeeded — trains managers to ignore the banner on
 * the screens where it is real.
 */
export function windowError(
  filters: ReportFilterValues,
  maxDays: number | null = REPORT_WINDOW_MAX_DAYS,
): string | null {
  if (!filters.from || !filters.to) return 'Pilih tanggal mulai dan tanggal akhir.';
  const span = daysBetween(filters.from, filters.to);
  if (span < 0) return 'Tanggal akhir harus setelah tanggal mulai.';
  if (maxDays !== null && span > maxDays) {
    return `Rentang maksimum ${maxDays} hari — saat ini ${span} hari.`;
  }
  return null;
}

/**
 * A list filter as the API reads it: ONE key holding every chosen id,
 * comma-separated — the shape `/fraud/flags`' `disposition` already takes — or
 * `undefined` for "Semua".
 *
 * One key rather than a repeated one because `withQuery` sets each key once, and
 * a singular key because a single id is still a valid value: the api's
 * `idListParam` parses both, so a URL or a queued export from before the filter
 * became a list still means what it meant.
 */
export function listParam(ids: readonly string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

/** `listParam` for the distributor filter — the name every caller already uses. */
export const regionParam = listParam;

/** `listParam` over picked options — the outlet, petugas and SKU pickers hold pairs. */
export function optionsParam(options: ReadonlyArray<{ id: string }>): string | undefined {
  return listParam(options.map((option) => option.id));
}

/** The query parameters, with empty selections dropped rather than sent blank. */
export function toQuery(filters: ReportFilterValues): Record<string, string | undefined> {
  return {
    from: startOfDayIso(filters.from),
    to: endOfDayIso(filters.to),
    regionId: regionParam(filters.regionIds),
    outletId: optionsParam(filters.outlets),
    salesUserId: optionsParam(filters.salesUsers),
    channelId: listParam(filters.channelIds),
    classificationId: listParam(filters.classificationIds),
  };
}

/**
 * The chosen classifications that still belong to a chosen channel — what is
 * left after a channel is unticked.
 *
 * A classification of a channel no longer chosen would narrow nothing the reader
 * can see (its channel is out of the report) while staying ticked in a list that
 * no longer shows it. One the list does not contain at all — still loading, or
 * deactivated since — is kept, for the reason `toggleSelection` keeps an unlisted
 * id: dropping a filter the reader cannot see widens the report silently.
 */
export function pruneClassifications(
  classifications: ReadonlyArray<{ id: string; channelId: string }>,
  channelIds: readonly string[],
  classificationIds: readonly string[],
): string[] {
  const channelOf = new Map(classifications.map((row) => [row.id, row.channelId]));
  const chosenChannels = new Set(channelIds);
  return classificationIds.filter((id) => {
    const channelId = channelOf.get(id);
    return channelId === undefined || chosenChannels.has(channelId);
  });
}

/** A stable react-query key for the current filter set. */
export function filterKey(filters: ReportFilterValues): string {
  return [
    filters.from,
    filters.to,
    filters.regionIds.join(','),
    optionsParam(filters.outlets) ?? '',
    optionsParam(filters.salesUsers) ?? '',
    filters.channelIds.join(','),
    filters.classificationIds.join(','),
  ].join('|');
}
